import axios from 'axios';
import { API_BASE_URL, REFRESH_ENDPOINT } from './endpoints';

const api = axios.create({
    baseURL: API_BASE_URL,
});

// Token management
const ACCESS_TOKEN_KEY = 'access';
const ID_TOKEN_KEY = 'id';
const REFRESH_TOKEN_KEY = 'refresh';

export function setTokens({ access, id, refresh }: { access: string; id: string; refresh: string }) {
    // Validate tokens before storing
    if (!access || !id || !refresh ||
        access === 'undefined' || id === 'undefined' || refresh === 'undefined' ||
        access === 'null' || id === 'null' || refresh === 'null') {
        console.error('Invalid tokens provided to setTokens:', { access, id, refresh });
        clearTokens();
        return;
    }

    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(ID_TOKEN_KEY, id);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function getAccessToken(): string | null {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    console.log('Access token:', token);

    // Return null if token is undefined, null, or empty string
    if (!token || token === 'undefined' || token === 'null') {
        return null;
    }

    return token;
}

export function getIdToken(): string | null {
    const token = localStorage.getItem(ID_TOKEN_KEY);

    // Return null if token is undefined, null, or empty string
    if (!token || token === 'undefined' || token === 'null') {
        return null;
    }

    return token;
}

export function getRefreshToken(): string | null {
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);

    // Return null if token is undefined, null, or empty string
    if (!token || token === 'undefined' || token === 'null') {
        return null;
    }

    return token;
}

export function clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('cart');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('selectedOrganizerId');
    localStorage.removeItem('impersonate');
}

// Attach id token and tenant id to requests
api.interceptors.request.use((config) => {
    let idToken;
    /* if (!localStorage.getItem('simulateRefreshToken')) {
        idToken = '111';
    }
    else {
        idToken = getIdToken();
        localStorage.removeItem('simulateRefreshToken');
    } */
    idToken = getIdToken();
    if (idToken) {
        config.headers = config.headers || {} as any;
        config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    const organizationId = localStorage.getItem('selectedOrganizerId');
    // Lets the backend attribute signed-out activity to a browser; auth-context removes it on login
    const guestId = localStorage.getItem('guestCartId');
    if (!idToken && guestId) {
        config.headers = config.headers || {} as any;
        config.headers['guest_id'] = guestId;
    }

    // Add tenant ID header
    config.headers = config.headers || {} as any;
    config.headers['tenant_id'] = 'tikestan'; // You can make this configurable if needed
    config.headers['organization_id'] = organizationId; // You can make this configurable if needed

    const impersonate = localStorage.getItem('impersonate');
    if (impersonate && idToken) config.headers['impersonate'] = impersonate;

    return config;
});

// Refresh token logic
let isRefreshing = false;
let refreshSubscribers: Array<(access: string) => void> = [];

function onRefreshed(access: string) {
    refreshSubscribers.forEach((cb) => cb(access));
    refreshSubscribers = [];

}

function addRefreshSubscriber(cb: (access: string) => void) {
    refreshSubscribers.push(cb);
}

export async function refreshToken() {
    const accessToken = getAccessToken();
    const idToken = getIdToken();
    const refreshToken = getRefreshToken();

    // Check if tokens exist and are not undefined/null
    if (!refreshToken || !idToken || !accessToken ||
        refreshToken === 'undefined' || idToken === 'undefined' || accessToken === 'undefined') {
        clearTokens();
        throw new Error('Missing or invalid tokens');
    }

    const response = await axios.post(
        `${API_BASE_URL}${REFRESH_ENDPOINT}`,
        {
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
        },
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    // Validate response data before setting tokens
    const { access_token, id_token, refresh_token } = response.data;
    if (!access_token || !id_token || !refresh_token ||
        access_token === 'undefined' || id_token === 'undefined' || refresh_token === 'undefined') {
        clearTokens();
        throw new Error('Invalid token response from server');
    }
    //localStorage.setItem('simulateRefreshToken', 'true');
    setTokens({ access: access_token, id: id_token, refresh: refresh_token });
    return id_token;
}

// Response interceptor to handle 401 errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Create the promise that will retry the request
            const retryPromise = new Promise((resolve, reject) => {
                addRefreshSubscriber((idToken: string) => {
                    originalRequest.headers['Authorization'] = `Bearer ${idToken}`;
                    originalRequest._retry = true;
                    // Retry the original request with new token
                    api(originalRequest).then(resolve).catch(reject);
                });
            });

            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const newIdToken = await refreshToken();
                    onRefreshed(newIdToken);
                    isRefreshing = false;
                } catch (err) {
                    clearTokens();
                    isRefreshing = false;
                    refreshSubscribers = []; // Clear subscribers on error
                    // Redirect to login or home page on token refresh failure
                    window.location.href = '/login';
                    return Promise.reject(err);
                }
            }

            return retryPromise;
        }
        return Promise.reject(error);
    }
);

export default api;